import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  ConsultationStore,
  ConsultationSession,
  ConsultationMessage,
  PersonaSettings,
  UserSettings,
  RoleplaySettings,
  ConsultationInput,
  ConsultationResult,
  ConferencePreset,
  UserSettingsPreset
} from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

// デフォルト制限値の定義
const DEFAULT_MAX_SESSIONS = 50
const DEFAULT_MAX_MESSAGES_PER_SESSION = 100
const DEFAULT_AUTO_SAVE = true

// ユーティリティ関数
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

// 追加の設定型
interface ConsultationSettings {
  autoSave: boolean
  maxSessions: number
  maxMessagesPerSession: number
  useGemini3Preview: boolean  // Gemini 3.0 Pro Preview使用（テスト運用・課金のみ）
  useGemini3FlashPreview: boolean  // Gemini 3.0 Flash Preview使用（テスト運用・課金のみ・高速）
  useGemini3FlashLitePreview: boolean  // Gemini 3.1 Flash Lite Preview使用（テスト運用・課金のみ・超軽量）
}

// AI分析結果のキャッシュ型
interface PersonaAnalysisCache {
  settings: PersonaSettings['settings']
  reasoning: Record<string, string>
  analyzedAt: string
}

interface ExtendedConsultationStore extends Omit<ConsultationStore, 'persist'> {
  settings: ConsultationSettings
  updateSettings: (settings: Partial<ConsultationSettings>) => void
  // AI分析キャッシュ
  personaAnalysisCache: Record<string, PersonaAnalysisCache>
  getCachedAnalysis: (characterId: string) => PersonaAnalysisCache | null
  setCachedAnalysis: (characterId: string, analysis: Omit<PersonaAnalysisCache, 'analyzedAt'> | null) => void
  clearAnalysisCache: () => void
  // 会議モード設定の永続化
  conferenceSettings: any | null
  setConferenceSettings: (settings: any | null) => void
  // キャラクターアイコン管理
  characterIcons: Record<string, string> // characterId -> base64 or URL
  getCharacterIcon: (characterId: string) => string | null
  setCharacterIcon: (characterId: string, icon: string | null) => void
  clearCharacterIcon: (characterId: string) => void
  clearAllCharacterIcons: () => void
  // ユーザーアイコン管理
  userIcon: string | null
  setUserIcon: (icon: string | null) => void
  // hydration状態
  isHydrated: boolean
  // 履歴圧縮
  compressSessionHistory: (sessionId: string, keepRecentCount?: number) => Promise<void>
  // まとめ生成（履歴を削除しない）
  generateSessionSummary: (sessionId: string) => Promise<{
    summary: string
    messageCount: number
    timeRange: {
      start: string
      end: string
    }
  }>
  // activeSessionを常に最新のデータで取得
  getCurrentActiveSession: () => ConsultationSession | null
  // セッション複製
  duplicateSession: (sessionId: string) => ConsultationSession | null
  // 会議プリセット管理
  conferencePresets: ConferencePreset[]
  saveConferencePreset: (name: string) => ConferencePreset | null
  loadConferencePreset: (presetId: string) => boolean
  deleteConferencePreset: (presetId: string) => void
  // ユーザー設定プリセット管理
  userSettingsPresets: UserSettingsPreset[]
  saveUserSettingsPreset: (name: string) => UserSettingsPreset | null
  loadUserSettingsPreset: (presetId: string) => boolean
  deleteUserSettingsPreset: (presetId: string) => void
  // ロールプレイ設定管理
  roleplaySettings: RoleplaySettings | null
  setRoleplaySettings: (settings: RoleplaySettings | null) => void
  updateRoleplaySettings: (settings: Partial<RoleplaySettings>) => void
}

export const useConsultationStore = create<ExtendedConsultationStore & { persist?: any }>()(
  persist(
    (set, get) => ({
      // 状態
      sessions: [],
      activeSession: null,
      personaSettings: null,
      userSettings: null,
      personaAnalysisCache: {},
      conferenceSettings: null,
      conferencePresets: [],
      userSettingsPresets: [],
      roleplaySettings: null,
      characterIcons: {},
      userIcon: null,
      isLoading: false,
      error: null,
      
      // 設定
      settings: {
        autoSave: DEFAULT_AUTO_SAVE,
        maxSessions: DEFAULT_MAX_SESSIONS,
        maxMessagesPerSession: DEFAULT_MAX_MESSAGES_PER_SESSION,
        useGemini3Preview: false,
        useGemini3FlashPreview: false,
        useGemini3FlashLitePreview: false
      },

      // 設定管理
      updateSettings: (newSettings) => {
        set(state => ({
          settings: {
            ...state.settings,
            ...newSettings
          }
        }))
      },
      
      // セッション管理
      createSession: (title?: string) => {
        const newSession: ConsultationSession = {
          id: generateSessionId(),
          title: title || `相談 ${new Date().toLocaleDateString()}`,
          messages: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        
        set(state => {
          let sessions = [newSession, ...state.sessions]
          // セッション数の制限
          if (sessions.length > state.settings.maxSessions) {
            sessions = sessions.slice(0, state.settings.maxSessions)
          }
          return {
            sessions,
            activeSession: newSession
          }
        })
        
        return newSession
      },
      
      loadSession: (sessionId: string) => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (session) {
          // activeSessionを最新のセッションデータで更新
          set({ activeSession: { ...session } })
        }
      },
      
      deleteSession: (sessionId: string) => {
        set(state => {
          const sessions = state.sessions.filter(s => s.id !== sessionId)
          const activeSession = state.activeSession?.id === sessionId 
            ? sessions[0] || null 
            : state.activeSession
          return { sessions, activeSession }
        })
      },
      
      updateSessionTitle: (sessionId: string, title: string) => {
        set(state => {
          const sessions = state.sessions.map(session => 
            session.id === sessionId
              ? { ...session, title, updatedAt: new Date().toISOString() }
              : session
          )
          
          // activeSessionも更新が必要な場合のみ更新
          const updatedState: any = { sessions }
          if (state.activeSession?.id === sessionId) {
            const updatedSession = sessions.find(s => s.id === sessionId)
            if (updatedSession) {
              updatedState.activeSession = updatedSession
            }
          }
          
          return updatedState
        })
      },
      
      clearAllSessions: () => {
        set({ sessions: [], activeSession: null })
      },

      duplicateSession: (sessionId: string) => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (!session) {
          return null
        }

        // 新しいセッションを作成
        const duplicatedSession: ConsultationSession = {
          id: generateSessionId(),
          title: `${session.title} のコピー`,
          messages: JSON.parse(JSON.stringify(session.messages)), // ディープコピー
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          metadata: session.metadata ? JSON.parse(JSON.stringify(session.metadata)) : undefined
        }

        set(state => {
          let sessions = [duplicatedSession, ...state.sessions]
          // セッション数の制限
          if (sessions.length > state.settings.maxSessions) {
            sessions = sessions.slice(0, state.settings.maxSessions)
          }
          return {
            sessions,
            activeSession: duplicatedSession
          }
        })

        return duplicatedSession
      },

      // AI分析キャッシュ管理
      getCachedAnalysis: (characterId: string) => {
        return get().personaAnalysisCache[characterId] || null
      },
      
      setCachedAnalysis: (characterId: string, analysis: Omit<PersonaAnalysisCache, 'analyzedAt'> | null) => {
        set(state => {
          if (analysis === null) {
            // キャッシュをクリア
            const { [characterId]: _, ...rest } = state.personaAnalysisCache
            return {
              personaAnalysisCache: rest
            }
          } else {
            // キャッシュを更新
            return {
              personaAnalysisCache: {
                ...state.personaAnalysisCache,
                [characterId]: {
                  ...analysis,
                  analyzedAt: new Date().toISOString()
                }
              }
            }
          }
        })
      },
      
      clearAnalysisCache: () => {
        set({ personaAnalysisCache: {} })
      },
      
      // 会議設定管理
      setConferenceSettings: (settings) => {
        set({ conferenceSettings: settings })
      },

      // 会議プリセット管理
      saveConferencePreset: (name: string) => {
        const currentSettings = get().conferenceSettings
        if (!currentSettings || !currentSettings.participants || currentSettings.participants.length < 2) {
          return null
        }

        const preset: ConferencePreset = {
          id: `preset_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name,
          participants: currentSettings.participants.map(p => ({
            characterId: p.characterId,
            characterName: p.characterName
          })),
          discussionStyle: currentSettings.discussionStyle,
          createdAt: new Date().toISOString()
        }

        set(state => ({
          conferencePresets: [...state.conferencePresets, preset]
        }))

        return preset
      },

      loadConferencePreset: (presetId: string) => {
        const preset = get().conferencePresets.find(p => p.id === presetId)
        if (!preset) return false

        // プリセットの情報を返す（実際のロードはConferenceSelectorで行う）
        return true
      },

      deleteConferencePreset: (presetId: string) => {
        set(state => ({
          conferencePresets: state.conferencePresets.filter(p => p.id !== presetId)
        }))
      },

      // ユーザー設定プリセット管理
      saveUserSettingsPreset: (name: string) => {
        const currentSettings = get().userSettings
        const currentIcon = get().userIcon
        const currentRoleplay = get().roleplaySettings
        if (!currentSettings || !currentSettings.enabled) {
          return null
        }

        const preset: UserSettingsPreset = {
          id: `user_preset_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name,
          settings: {
            name: currentSettings.name || '',
            profile: currentSettings.profile || ''
          },
          icon: currentIcon || undefined,
          roleplaySettings: currentRoleplay || undefined,
          createdAt: new Date().toISOString()
        }

        set(state => ({
          userSettingsPresets: [...state.userSettingsPresets, preset]
        }))

        return preset
      },

      loadUserSettingsPreset: (presetId: string) => {
        const preset = get().userSettingsPresets.find(p => p.id === presetId)
        if (!preset) return false

        // プリセットの設定を適用（ロールプレイ設定も含む）
        set({
          userSettings: {
            enabled: true,
            name: preset.settings.name,
            profile: preset.settings.profile
          },
          userIcon: preset.icon || null,
          roleplaySettings: preset.roleplaySettings || null
        })

        return true
      },

      deleteUserSettingsPreset: (presetId: string) => {
        set(state => ({
          userSettingsPresets: state.userSettingsPresets.filter(p => p.id !== presetId)
        }))
      },

      // ロールプレイ設定管理
      setRoleplaySettings: (settings: RoleplaySettings | null) => {
        set({ roleplaySettings: settings })
      },

      updateRoleplaySettings: (settings: Partial<RoleplaySettings>) => {
        set(state => ({
          roleplaySettings: state.roleplaySettings
            ? { ...state.roleplaySettings, ...settings }
            : { enabled: false, ...settings }
        }))
      },

      // キャラクターアイコン管理
      getCharacterIcon: (characterId: string) => {
        return get().characterIcons[characterId] || null
      },
      
      setCharacterIcon: (characterId: string, icon: string | null) => {
        if (icon === null) {
          const { [characterId]: _, ...rest } = get().characterIcons
          set({ characterIcons: rest })
        } else {
          set({ 
            characterIcons: {
              ...get().characterIcons,
              [characterId]: icon
            }
          })
        }
      },
      
      clearCharacterIcon: (characterId: string) => {
        const { [characterId]: _, ...rest } = get().characterIcons
        set({ characterIcons: rest })
      },
      
      clearAllCharacterIcons: () => {
        set({ characterIcons: {} })
      },
      
      // メッセージ管理
      addMessage: (sessionId: string, message: ConsultationMessage) => {
        
        set(state => {
          const sessions = state.sessions.map(session => {
            if (session.id === sessionId) {
              let messages = [...session.messages, message]
              
              // メッセージ数の制限
              if (messages.length > state.settings.maxMessagesPerSession) {
                // 古いメッセージを削除（最初のシステムメッセージは保持）
                const systemMessages = messages.filter(m => m.role === 'system')
                const nonSystemMessages = messages.filter(m => m.role !== 'system')
                messages = [
                  ...systemMessages,
                  ...nonSystemMessages.slice(-state.settings.maxMessagesPerSession + systemMessages.length)
                ]
              }
              
              // 最初のユーザーメッセージの場合、タイトルを自動生成
              let updatedTitle = session.title
              if (message.role === 'user' && session.messages.length === 0) {
                // デフォルトタイトルの場合のみ自動生成
                if (session.title.startsWith('相談 ')) {
                  // メッセージ内容を整形（改行・余分なスペースを除去）
                  const cleanContent = message.content
                    .replace(/\n+/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim()
                  
                  // タイトル生成のロジック
                  let title = ''
                  
                  // キーワードで区切る（「について」「に関して」など）
                  const keywordPatterns = [
                    /(.{1,40}について)/,
                    /(.{1,40}に関して)/,
                    /(.{1,40}の件)/,
                    /(.{1,40}を教えて)/,
                    /(.{1,40}はどう)/,
                    /(.{1,40}ですか)/,
                    /(.{1,40}ますか)/
                  ]
                  
                  let matched = false
                  for (const pattern of keywordPatterns) {
                    const match = cleanContent.match(pattern)
                    if (match) {
                      title = match[1]
                      matched = true
                      break
                    }
                  }
                  
                  // キーワードがない場合は最初の50文字
                  if (!matched) {
                    title = cleanContent.slice(0, 50)
                  }
                  
                  // 最終調整
                  if (title.length > 0) {
                    updatedTitle = title + (cleanContent.length > title.length ? '...' : '')
                  }
                }
              }
              
              return {
                ...session,
                title: updatedTitle,
                messages,
                updatedAt: new Date().toISOString()
              }
            }
            return session
          })
          
          // activeSessionも更新が必要な場合のみ更新
          const updatedState: any = { sessions }
          if (state.activeSession?.id === sessionId) {
            const updatedSession = sessions.find(s => s.id === sessionId)
            if (updatedSession) {
              updatedState.activeSession = updatedSession
            }
          }
          
          return updatedState
        })
      },
      
      deleteMessage: (sessionId: string, messageId: string) => {
        set(state => {
          const sessions = state.sessions.map(session => 
            session.id === sessionId
              ? {
                  ...session,
                  messages: session.messages.filter(m => m.id !== messageId),
                  updatedAt: new Date().toISOString()
                }
              : session
          )
          
          // activeSessionも更新が必要な場合のみ更新
          const updatedState: any = { sessions }
          if (state.activeSession?.id === sessionId) {
            const updatedSession = sessions.find(s => s.id === sessionId)
            if (updatedSession) {
              updatedState.activeSession = updatedSession
            }
          }
          
          return updatedState
        })
      },
      
      editMessage: (sessionId: string, messageId: string, content: string) => {
        set(state => {
          const sessions = state.sessions.map(session => 
            session.id === sessionId
              ? {
                  ...session,
                  messages: session.messages.map(m => 
                    m.id === messageId
                      ? { ...m, content, timestamp: new Date().toISOString() }
                      : m
                  ),
                  updatedAt: new Date().toISOString()
                }
              : session
          )
          
          // activeSessionも更新が必要な場合のみ更新
          const updatedState: any = { sessions }
          if (state.activeSession?.id === sessionId) {
            const updatedSession = sessions.find(s => s.id === sessionId)
            if (updatedSession) {
              updatedState.activeSession = updatedSession
            }
          }
          
          return updatedState
        })
      },
      
      // ユーザー設定管理
      setUserSettings: (settings: UserSettings) => {
        set({ userSettings: settings })
      },
      
      updateUserSettings: (settings: Partial<UserSettings>) => {
        set(state => ({
          userSettings: state.userSettings
            ? { ...state.userSettings, ...settings }
            : { enabled: false, ...settings }
        }))
      },
      
      clearUserSettings: () => {
        set({ userSettings: null, userIcon: null })
      },
      
      // ユーザーアイコン管理
      setUserIcon: (icon: string | null) => {
        set({ userIcon: icon })
      },
      
      // ペルソナ管理
      setPersona: (settings: PersonaSettings) => {
        set({ personaSettings: settings })
      },
      
      clearPersona: () => {
        set({ personaSettings: null })
      },
      
      updatePersonaSettings: (settings: Partial<PersonaSettings['settings']>) => {
        set(state => ({
          personaSettings: state.personaSettings
            ? {
                ...state.personaSettings,
                settings: { ...state.personaSettings.settings, ...settings }
              }
            : null
        }))
      },
      
      // エクスポート/インポート
      exportSession: (sessionId: string, format: 'json' | 'text' = 'json') => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (!session) return
        
        const date = new Date().toISOString().split('T')[0] // YYYY-MM-DD形式
        const cleanTitle = session.title.replace(/[^ぁ-んァ-ンa-zA-Z0-9]/gi, '_').slice(0, 30)
        
        if (format === 'text') {
          // テキスト形式でエクスポート
          let text = `# 相談セッション: ${session.title}\n\n`
          text += `作成日: ${new Date(session.createdAt).toLocaleString('ja-JP')}\n`
          text += `更新日: ${new Date(session.updatedAt).toLocaleString('ja-JP')}\n`
          text += `メッセージ数: ${session.messages.length}件\n\n`
          text += '━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n'
          
          for (const msg of session.messages) {
            const role = msg.role === 'user' ? '👤 あなた' : 
                         msg.role === 'system' ? '📋 システム' :
                         `🤖 ${msg.metadata?.personaName || 'AI'}`
            const time = new Date(msg.timestamp).toLocaleTimeString('ja-JP', {
              hour: '2-digit',
              minute: '2-digit'
            })
            text += `【${role}】 ${time}\n`
            text += '─'.repeat(40) + '\n'
            text += `${msg.content}\n\n`
          }
          
          const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `相談ログ_${cleanTitle}_${date}.txt`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        } else {
          // JSON形式でエクスポート（インポート可能な形式）
          const sessionData = {
            version: '1.0',
            exportDate: new Date().toISOString(),
            sessions: [session],
            metadata: {
              totalSessions: 1,
              totalMessages: session.messages.length
            }
          }
          
          const json = JSON.stringify(sessionData, null, 2)
          const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `相談データ_${cleanTitle}_${date}.json`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        }
      },
      
      exportAllSessions: () => {
        const data = {
          version: '1.0',
          exportDate: new Date().toISOString(),
          sessions: get().sessions,
          personaSettings: get().personaSettings,
          metadata: {
            totalSessions: get().sessions.length,
            totalMessages: get().sessions.reduce((acc, s) => acc + s.messages.length, 0)
          }
        }
        return JSON.stringify(data, null, 2)
      },
      
      importSessions: (jsonString: string) => {
        try {
          const data = JSON.parse(jsonString)
          const sessions = Array.isArray(data) ? data : data.sessions || []
          
          const validSessions = sessions
            .filter((s: any) => s.id && s.messages && Array.isArray(s.messages))
            .map((s: any) => ({
              ...s,
              id: s.id || generateSessionId(),
              createdAt: s.createdAt || new Date().toISOString(),
              updatedAt: s.updatedAt || new Date().toISOString()
            }))
          
          // ペルソナ設定のインポート（存在する場合）
          if (data.personaSettings) {
            set({ personaSettings: data.personaSettings })
          }
          
          // セッションのマージ
          set(state => {
            const existingIds = new Set(state.sessions.map(s => s.id))
            const newSessions = validSessions.filter((s: ConsultationSession) => !existingIds.has(s.id))
            let mergedSessions = [...newSessions, ...state.sessions]
            
            // セッション数の制限
            if (mergedSessions.length > state.settings.maxSessions) {
              mergedSessions = mergedSessions.slice(0, state.settings.maxSessions)
            }
            
            return { sessions: mergedSessions }
          })
          
          alert(`${validSessions.length}件のセッションをインポートしました`)
        } catch (error) {
          console.error('Import error:', error)
          throw new Error('インポートデータの形式が不正です')
        }
      },
      
      // ユーティリティ
      getSessionById: (sessionId: string) => {
        return get().sessions.find(s => s.id === sessionId)
      },
      
      getRecentSessions: (limit: number = 10) => {
        return get().sessions
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
          .slice(0, limit)
      },
      
      countTotalMessages: () => {
        return get().sessions.reduce((acc, session) => acc + session.messages.length, 0)
      },
      
      // API連携（フロントエンドから実装）
      sendMessage: async (input: ConsultationInput): Promise<ConsultationResult> => {
        set({ isLoading: true, error: null })
        
        try {
          // この実装はフロントエンドコンポーネントで行う
          // ここではプレースホルダーとしてエラーを投げる
          throw new Error('sendMessage should be implemented in the component with API client')
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Unknown error' 
          })
          throw error
        }
      },
      
      streamMessage: async (
        input: ConsultationInput, 
        onChunk: (chunk: string) => void
      ): Promise<ConsultationResult> => {
        set({ isLoading: true, error: null })
        
        try {
          // この実装はフロントエンドコンポーネントで行う
          throw new Error('streamMessage should be implemented in the component with API client')
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Unknown error' 
          })
          throw error
        }
      },
      
      regenerateLastMessage: async (sessionId: string): Promise<ConsultationResult> => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (!session || session.messages.length === 0) {
          throw new Error('セッションまたはメッセージが見つかりません')
        }
        
        // 最後のアシスタントメッセージを削除
        const lastAssistantIndex = session.messages.findLastIndex(m => m.role === 'assistant')
        if (lastAssistantIndex === -1) {
          throw new Error('再生成するメッセージがありません')
        }
        
        // メッセージを削除してから再送信
        get().deleteMessage(sessionId, session.messages[lastAssistantIndex].id)
        
        // 最後のユーザーメッセージを取得
        const lastUserMessage = session.messages.findLast(m => m.role === 'user')
        if (!lastUserMessage) {
          throw new Error('ユーザーメッセージが見つかりません')
        }
        
        // 再送信（実装はコンポーネントで行う）
        return get().sendMessage({
          message: lastUserMessage.content,
          sessionId,
          personaSettings: get().personaSettings || undefined,
          chatHistory: session.messages.slice(0, lastAssistantIndex)
        })
      },
      
      // hydration状態
      isHydrated: false,
      
      // activeSessionを常に最新のデータで取得
      getCurrentActiveSession: () => {
        const state = get()
        if (!state.activeSession) return null
        
        // sessionsから最新のデータを取得
        const currentSession = state.sessions.find(s => s.id === state.activeSession?.id)
        return currentSession || null
      },
      
      // 履歴圧縮
      compressSessionHistory: async (sessionId: string, keepRecentCount: number = 20) => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (!session) {
          throw new Error('セッションが見つかりません')
        }
        
        // メッセージ数が少ない場合は圧縮不要
        if (session.messages.length <= keepRecentCount + 1) {
          throw new Error(`メッセージ数が不足しています。圧縮には最低${keepRecentCount + 1}件のメッセージが必要です。`)
        }
        
        set({ isLoading: true, error: null })
        
        try {
          // API呼び出しで圧縮を実行（動的インポート）
          const { apiClient } = await import('../services/api')
          const result = await apiClient.compressHistory({
            messages: session.messages,
            keepRecentCount,
            personaSettings: get().personaSettings,
            conferenceSettings: get().conferenceSettings
          })
          
          // 要約が正常に生成されたかチェック
          if (!result.compressedSummary || result.compressedSummary.trim() === '') {
            throw new Error('要約の生成に失敗しました。レスポンスが空です。')
          }
          
          // 圧縮結果をもとにセッションを更新
          const messagesToKeep = session.messages.slice(-keepRecentCount)
          const summaryMessage: ConsultationMessage = {
            id: `summary_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
            role: 'system',
            content: result.compressedSummary,
            timestamp: new Date().toISOString()
          }
          
          // 新しいメッセージ配列を作成
          const newMessages = [summaryMessage, ...messagesToKeep]
          
          // セッションを更新（成功時のみ）
          set(state => {
            const updatedSessions = state.sessions.map(s => 
              s.id === sessionId
                ? { ...s, messages: newMessages, updatedAt: new Date().toISOString() }
                : s
            )
            
            // activeSessionは必要な場合のみ更新
            const updatedState: any = {
              sessions: updatedSessions,
              isLoading: false
            }
            
            if (state.activeSession?.id === sessionId) {
              const updatedSession = updatedSessions.find(s => s.id === sessionId)
              if (updatedSession) {
                updatedState.activeSession = updatedSession
              }
            }
            
            return updatedState
          })
          
          console.log(`履歴圧縮完了: ${result.originalMessageCount}件 → ${newMessages.length}件`)
        } catch (error) {
          console.error('履歴圧縮エラー:', error)
          
          // エラー時はメッセージを削除せず、エラー状態だけを更新
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : '履歴圧縮に失敗しました' 
          })
          
          // ユーザーに通知
          alert(`履歴圧縮に失敗しました。メッセージは保持されます。\n\nエラー: ${error instanceof Error ? error.message : '不明なエラー'}`)
          
          throw error
        }
      },
      
      // まとめ生成（履歴を削除しない議事録形式）
      generateSessionSummary: async (sessionId: string) => {
        const session = get().sessions.find(s => s.id === sessionId)
        if (!session) {
          throw new Error('セッションが見つかりません')
        }
        
        // メッセージが少ない場合の警告
        if (session.messages.length === 0) {
          throw new Error('まとめを作成する会話がありません')
        }
        
        set({ isLoading: true, error: null })
        
        try {
          // API呼び出しでまとめを生成（動的インポート）
          const { apiClient } = await import('../services/api')
          const result = await apiClient.generateSummary({
            messages: session.messages,
            personaSettings: get().personaSettings,
            conferenceSettings: get().conferenceSettings
          })
          
          // まとめが正常に生成されたかチェック
          if (!result.summary || result.summary.trim() === '') {
            throw new Error('まとめの生成に失敗しました。レスポンスが空です。')
          }
          
          set({ isLoading: false })
          
          console.log(`まとめ生成完了: ${result.messageCount}件のメッセージをまとめました`)
          
          return result
        } catch (error) {
          console.error('まとめ生成エラー:', error)
          
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'まとめ生成に失敗しました' 
          })
          
          // ユーザーに通知
          alert(`まとめ生成に失敗しました。\n\nエラー: ${error instanceof Error ? error.message : '不明なエラー'}`)
          
          throw error
        }
      }
    }),
    {
      name: 'consultation-storage',
      version: 1,
      storage: createIndexedDBStorage('consultation-storage'),
      onRehydrateStorage: () => (state) => {
        // hydration完了時にフラグを立てる
        if (state) {
          state.isHydrated = true
        }
      },
      partialize: (state) => ({
        sessions: state.sessions,
        activeSessionId: state.activeSession?.id || null,  // IDのみ保存（データの重複を防ぐ）
        personaSettings: state.personaSettings,
        userSettings: state.userSettings,
        userIcon: state.userIcon,
        roleplaySettings: state.roleplaySettings,  // ロールプレイ設定
        settings: state.settings,
        personaAnalysisCache: state.personaAnalysisCache,  // 通常のObjectなのでそのまま
        conferenceSettings: state.conferenceSettings,
        conferencePresets: state.conferencePresets,  // 会議プリセット
        userSettingsPresets: state.userSettingsPresets,  // ユーザー設定プリセット
        characterIcons: state.characterIcons  // 通常のObjectなのでそのまま
        // 注意: isHydratedは永続化しない（実行時の状態フラグのため）
      }),
      migrate: (persistedState: any, version: number) => {
        // persistedStateが完全に空の場合（初回起動時）のみデフォルト値を返す
        if (!persistedState || Object.keys(persistedState).length === 0) {
          console.log('ConsultationStore: Initializing with default values')
          return {
            sessions: [],
            activeSession: null,
            personaSettings: null,
            userSettings: null,
            userIcon: null,
            roleplaySettings: null,
            settings: {
              autoSave: DEFAULT_AUTO_SAVE,
              maxSessions: DEFAULT_MAX_SESSIONS,
              maxMessagesPerSession: DEFAULT_MAX_MESSAGES_PER_SESSION,
              useGemini3Preview: false,
              useGemini3FlashPreview: false,
              useGemini3FlashLitePreview: false
            },
            personaAnalysisCache: {},
            conferenceSettings: null,
            conferencePresets: [],
            userSettingsPresets: [],
            characterIcons: {},
            isLoading: false,
            error: null
          }
        }

        // conferencePresetsがない場合のマイグレーション
        if (!persistedState.conferencePresets) {
          persistedState.conferencePresets = []
        }

        // userSettingsPresetsがない場合のマイグレーション
        if (!persistedState.userSettingsPresets) {
          persistedState.userSettingsPresets = []
        }

        // roleplaySettingsがない場合のマイグレーション
        if (persistedState.roleplaySettings === undefined) {
          persistedState.roleplaySettings = null
        }

        // 既存データにuseGemini3Preview/useGemini3FlashPreviewがない場合のフォールバック
        if (persistedState.settings && persistedState.settings.useGemini3Preview === undefined) {
          persistedState.settings.useGemini3Preview = false
        }
        if (persistedState.settings && persistedState.settings.useGemini3FlashPreview === undefined) {
          persistedState.settings.useGemini3FlashPreview = false
        }
        if (persistedState.settings && persistedState.settings.useGemini3FlashLitePreview === undefined) {
          persistedState.settings.useGemini3FlashLitePreview = false
        }

        // 既存データがある場合の移行処理
        // 旧形式（activeSession）から新形式（activeSessionId）への移行
        if (persistedState.activeSession && !persistedState.activeSessionId) {
          persistedState.activeSessionId = persistedState.activeSession.id
          delete persistedState.activeSession
        }

        // activeSessionIdの整合性チェック
        if (persistedState.activeSessionId && persistedState.sessions) {
          const sessionExists = persistedState.sessions.some((s: any) =>
            s.id === persistedState.activeSessionId
          )

          if (!sessionExists) {
            // activeSessionIdが存在しない場合は、最新のセッションを使用
            console.warn('Active session ID not found in sessions list. Resetting to latest session.')
            const sortedSessions = [...persistedState.sessions].sort((a: any, b: any) =>
              new Date(b.updatedAt || b.createdAt).getTime() -
              new Date(a.updatedAt || a.createdAt).getTime()
            )
            persistedState.activeSessionId = sortedSessions[0]?.id || null
          }
        }

        // activeSessionを実際のセッションオブジェクトとして復元
        if (persistedState.activeSessionId && persistedState.sessions) {
          persistedState.activeSession = persistedState.sessions.find((s: any) =>
            s.id === persistedState.activeSessionId
          ) || null
        } else {
          persistedState.activeSession = null
        }

        // settings フィールドの修正（compressionThresholdは誤り）
        if (persistedState.settings && !persistedState.settings.autoSave) {
          persistedState.settings = {
            autoSave: DEFAULT_AUTO_SAVE,
            maxSessions: persistedState.settings.maxSessions || DEFAULT_MAX_SESSIONS,
            maxMessagesPerSession: persistedState.settings.maxMessagesPerSession || DEFAULT_MAX_MESSAGES_PER_SESSION
          }
        }

        // 既存データはそのまま保持（デフォルト値での上書きはしない）
        return persistedState as ConsultationStore
      }
    }
  )
)